import {
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions
} from "@tanstack/react-query";

import {
  CheckProviderResponse,
  ForgotPasswordFormData,
  LoginPayload,
  LoginResponse,
  PasswordFormPayload,
  PasswordFormResponse,
  SsoTokenRequestPayload,
  SsoTokenRequestResponse
} from "@/types/auth";
import { CustomError } from "@/utils/errors";

import {
  getAuthStatus,
  getSsoProvider,
  loginApi,
  patchChangePassword,
  postActivateUser,
  postForgotPassword,
  postLogout,
  postResetPasword,
  postVerifyToken,
  ssoTokenRequest
} from "./authApi";

export const AUTH_KEYS = {
  user: ["auth", "user"] as const,
  login: ["auth", "login"] as const,
  activateUser: ["auth", "activateUser"] as const,
  resetPassword: ["auth", "resetPassword"] as const,
  forgotPassword: ["auth", "forgotPassword"] as const,
  verifyToken: ["auth", "verifyToken"] as const,
  logout: ["auth", "logout"] as const,
  authStatus: ["auth", "status"] as const
} as const;

export const useLoginMutation = (
  options?: UseMutationOptions<
    LoginResponse,
    { message: CustomError },
    LoginPayload
  >
) => {
  return useMutation({
    mutationFn: (payload: LoginPayload) => loginApi(payload),
    ...options
  });
};

export const useActivateUserMutation = (
  options?: Omit<
    UseMutationOptions<
      PasswordFormResponse,
      { message: CustomError },
      PasswordFormPayload
    >,
    "mutationKey" | "mutationFn"
  >
) => {
  return useMutation({
    mutationKey: AUTH_KEYS.activateUser,
    mutationFn: postActivateUser,
    ...options
  });
};

export const useResetPasswordMutation = (
  options?: Omit<
    UseMutationOptions<
      PasswordFormResponse,
      { message: CustomError },
      PasswordFormPayload
    >,
    "mutationKey" | "mutationFn"
  >
) => {
  return useMutation({
    mutationKey: AUTH_KEYS.resetPassword,
    mutationFn: postResetPasword,
    ...options
  });
};

export const useForgotPasswordMutation = (
  options?: Omit<
    UseMutationOptions<null, { message: CustomError }, ForgotPasswordFormData>,
    "mutationKey" | "mutationFn"
  >
) => {
  return useMutation({
    mutationKey: AUTH_KEYS.forgotPassword,
    mutationFn: postForgotPassword,
    ...options
  });
};

export const useVerifyTokenQuery = (
  token: string,
  options?: Omit<
    UseQueryOptions<unknown, { message: CustomError }, string>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: AUTH_KEYS.verifyToken,
    queryFn: async () => await postVerifyToken(token),
    retry: false,
    ...options
  });
};

export const useLogoutMutation = (
  options?: Omit<
    UseMutationOptions<unknown, { message: CustomError }>,
    "mutationKey" | "mutationFn"
  >
) => {
  return useMutation({
    mutationKey: AUTH_KEYS.logout,
    mutationFn: postLogout,
    ...options
  });
};

export const useChangePasswordMutation = (
  options?: UseMutationOptions<
    null,
    CustomError,
    { currentPassword: string; newPassword: string }
  >
) =>
  useMutation({
    mutationFn: (params: { currentPassword: string; newPassword: string }) =>
      patchChangePassword(params),
    ...options
  });

export const useGetSsoProviderMutation = (
  options?: UseMutationOptions<
    CheckProviderResponse,
    { message: CustomError },
    string
  >
) => {
  return useMutation({
    mutationFn: (domain: string) => getSsoProvider(domain),
    ...options
  });
};

export const useSsoTokenRequestMutation = (
  options?: UseMutationOptions<
    SsoTokenRequestResponse,
    { message: CustomError },
    { requestUrl: string; payload: SsoTokenRequestPayload }
  >
) => {
  return useMutation({
    mutationFn: ({
      requestUrl,
      payload
    }: {
      requestUrl: string;
      payload: SsoTokenRequestPayload;
    }) => ssoTokenRequest(requestUrl, payload),
    ...options
  });
};

export const useGetAuthStatus = (
  options?: Omit<
    UseQueryOptions<unknown, { message: CustomError }, string>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: AUTH_KEYS.authStatus,
    queryFn: getAuthStatus,
    retry: false,
    ...options
  });
};
